import React from 'react';
import {
  ErrorMonitorBoundary,
  useEnvironment,
  useTranslation,
  WidgetProps,
} from '@wix/yoshi-flow-editor';
import { WixCommentsApiProvider } from '@wix/comments-ooi-client';
import { Router } from '@wix/tpa-router/react';

import { SocialGroupStore } from 'store/SocialGroupsStore';

import { TPAComponentsProvider } from 'wix-ui-tpa/cssVars';

import { PortalProvider } from 'wui/Portal/context';
import { ErrorState } from 'wui/ErrorState';

import type { IControllerProps } from 'controller/types';
import { ControllerProvider } from 'common/context/controller';
import { FakeResponsiveProvider } from 'common/context/fake-responsive';

import { Toasts } from '../Toasts';
import { GroupMembershipDialogs } from '../GroupMembership';

interface ISocialGroupsAppProps extends WidgetProps<IControllerProps> {
  children: React.ReactNode;

  className?: string;
}

export function SocialGroupsApp(props: ISocialGroupsAppProps) {
  const { children, store, className, ...rest } = props;
  const { t } = useTranslation();
  const { isRTL, isMobile, isCssPerBreakpoint } = useEnvironment();

  const tpaComponentsEnv = React.useMemo(
    () => ({
      mobile: isMobile,
      rtl: isRTL,
      cssVars: true,
    }),
    [isMobile, isRTL],
  );

  return (
    <div
      className={className}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-hook="social-groups-app"
    >
      <ErrorMonitorBoundary
        isDebug
        fallback={
          <ErrorState
            title={t('groups-web.error-state.title')}
            subtitle={t('groups-web.error-state.subtitle')}
            actionLabel={t('groups-web.error-state.retry.label')}
          />
        }
      >
        <SocialGroupStore state={store}>
          <Router {...rest}>
            <PortalProvider host={props.host}>
              <ControllerProvider {...rest}>
                <WixCommentsApiProvider {...rest}>
                  <TPAComponentsProvider value={tpaComponentsEnv}>
                    {children}
                    <Toasts />
                    <GroupMembershipDialogs />
                  </TPAComponentsProvider>
                </WixCommentsApiProvider>
              </ControllerProvider>
            </PortalProvider>
          </Router>
        </SocialGroupStore>
      </ErrorMonitorBoundary>
    </div>
  );
}

SocialGroupsApp.displayName = 'SocialGroupsApp';
